import { createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, createBlock as _createBlock, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass, mergeProps as _mergeProps } from "vue"

const _hoisted_1 = {
  "data-wizard-header": "",
  class: "-mx-4 -my-1.5 flex min-w-0 flex-1 items-center justify-between gap-4 bg-white px-6 py-3.5 md:px-7"
}
const _hoisted_2 = { class: "flex min-w-0 flex-col gap-0.5" }
const _hoisted_3 = {
  "aria-label": "Breadcrumb",
  class: "text-[13px] text-gray-500"
}
const _hoisted_4 = { class: "flex min-w-0 items-center gap-2.5" }
const _hoisted_5 = {
  "data-top": "",
  class: "m-0 truncate pb-0.5 text-[22px] font-bold leading-snug tracking-[-0.02em] text-gray-900"
}
const _hoisted_6 = { class: "flex flex-shrink-0 items-center gap-2" }
const _hoisted_7 = { class: "flex h-full bg-[#f6f7f9]" }
const _hoisted_8 = { class: "flex min-h-0 min-w-0 flex-1 flex-col" }
const _hoisted_9 = { class: "flex-1 overflow-y-auto px-6 py-9 md:px-10" }
const _hoisted_10 = { class: "mx-auto max-w-[920px]" }
const _hoisted_11 = { class: "flex flex-col gap-1.5 pb-7" }
const _hoisted_12 = {
  "data-eyebrow": "",
  class: "m-0 text-[13px] font-semibold text-gray-500"
}
const _hoisted_13 = {
  "data-step-question": "",
  class: "m-0 text-2xl font-bold tracking-[-0.02em] text-gray-900"
}
const _hoisted_14 = {
  key: 0,
  class: "m-0 mt-1 max-w-2xl text-sm leading-relaxed text-gray-600"
}
const _hoisted_15 = {
  key: 0,
  "data-preview-error": "",
  class: "mb-6 rounded-lg bg-amber-50 px-4 py-3 text-sm text-amber-800"
}
const _hoisted_16 = { "data-step": "" }
const _hoisted_17 = {
  "data-wizard-footer": "",
  class: "border-t border-gray-200 bg-white px-6 py-3.5 md:px-12"
}
const _hoisted_18 = {
  "data-next-dates": "",
  class: "mt-4"
}
const _hoisted_19 = { class: "flex flex-wrap gap-1.5" }

import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'

import WizardButton from '@/components/neue/wizard/WizardButton.vue'
import WizardConfirmModal from '@/components/neue/wizard/WizardConfirmModal.vue'
import WizardNav from '@/components/neue/wizard/WizardNav.vue'
import WizardRail from '@/components/neue/wizard/WizardRail.vue'
import SecondaryNav from '@/components/SecondaryNav.vue'
import { useConfirm } from '@/composables/useConfirm'
import { useDropWizard } from '@/composables/useDropWizard'
import { provideHighlightTerms } from '@/composables/useHighlightTerms'
import { useToast } from '@/composables/useToast'
import { provideWizard } from '@/composables/useWizardContext'
import { dropLabel, stepHeading, stepSubtitle } from '@/config/dropWizardSteps'
import { formatDate } from '@/helpers/dropWizardDates'
import { describeSetup, railRows } from '@/helpers/dropWizardSummary'
import api from '@/store/api'
import { mapRelationship, mapRelationships, mapType } from '@/store/mappers'
import StepClose from '@/view/store/dropWizard/steps/StepClose.vue'
import StepDay from '@/view/store/dropWizard/steps/StepDay.vue'
import StepGet from '@/view/store/dropWizard/steps/StepGet.vue'
import StepOften from '@/view/store/dropWizard/steps/StepOften.vue'
import StepPay from '@/view/store/dropWizard/steps/StepPay.vue'
import StepReview from '@/view/store/dropWizard/steps/StepReview.vue'
import StepWhat from '@/view/store/dropWizard/steps/StepWhat.vue'
import StepWho from '@/view/store/dropWizard/steps/StepWho.vue'

const STORES = 'stores?included=none'
const METHODS = 'fulfillment_methods?included=fulfillment_method_store_pairs,delivery_address'
const TIERS = 'membership_tiers?included=audience.audience_detail'
const ENTER_OWNERS = 'textarea, button, a, [role="button"], [role="listbox"], select'


export default {
  __name: 'DropWizard',
  setup(__props, { expose: __expose }) {

const router = useRouter()
const store = useStore()
const { showToast } = useToast()

const tz = computed(() => store.state.loggedInUser?.attributes?.time_zone || undefined)

const wizard = useDropWizard()
provideWizard(wizard)

const stepKey = computed(() => wizard.stepKey.value)
const onReview = computed(() => stepKey.value === 'review')
const heading = computed(() => stepHeading(stepKey.value, wizard.data))
const subtitle = computed(() => stepSubtitle(stepKey.value, wizard.data))
const title = computed(() => wizard.data.name.trim() || 'New drop')
const nextStep = computed(() => wizard.steps.value[wizard.step.value.index + 1])
const primaryLabel = computed(() =>
  onReview.value
    ? 'Create drop'
    : nextStep.value?.key === 'review'
      ? 'Review'
      : `Continue to ${nextStep.value?.label ?? 'Review'}`
)
const eyebrow = computed(() =>
  onReview.value
    ? 'Review'
    : `Step ${wizard.step.value.index + 1} of ${wizard.questionCount.value} · ${wizard.step.value.label}`
)
const railOpen = ref(false)
const railButtonLabel = computed(() =>
  onReview.value ? 'Setup' : `Step ${wizard.step.value.index + 1} of ${wizard.questionCount.value}`
)
const visited = ref(new Set())
watch(stepKey, key => (visited.value = new Set([...visited.value, key])), { immediate: true })

// reference data

const stores = computed(() =>
  mapType('store').map(s => ({
    id: String(s.id),
    name: s.attributes.store_name,
    status: s.attributes.status === 'live' ? 'Active' : 'Private',
    productCount: s.attributes.products_count ?? s.relationships?.products?.data?.length ?? 0
  }))
)
const storeName = computed(() => stores.value.find(s => s.id === String(wizard.data.storeId))?.name ?? 'your store')

const typeOf = method => (method.attributes.type === 'PickupFulfillmentMethod' ? 'pickup' : 'delivery')
const detailOf = method => {
  if (typeOf(method) === 'pickup') {
    const address = mapRelationship(method.relationships?.delivery_address?.data)
    return address ? `Pickup · ${address.attributes.address1}, ${address.attributes.city}` : 'Pickup'
  }
  const zips = method.attributes.eligible_zip_codes ?? []
  const states = method.attributes.eligible_states ?? []
  if (method.attributes.use_bounding_polygon_waypoints) return 'Delivery · a drawn area'
  if (zips.length) return `Delivery · ${zips.length} zip ${zips.length === 1 ? 'code' : 'codes'}`
  if (states.length) return `Delivery · ${states.length} ${states.length === 1 ? 'state' : 'states'}`
  return 'Delivery · anywhere'
}
const storeMethods = computed(() =>
  mapType('fulfillment_method')
    .filter(method => method.attributes.is_active !== false)
    .filter(method =>
      mapRelationships(method.relationships?.fulfillment_method_store_pairs?.data).some(
        pair => String(pair.attributes.store_id) === String(wizard.data.storeId)
      )
    )
    .map(method => ({
      id: String(method.id),
      name: method.attributes.name,
      type: typeOf(method),
      detail: detailOf(method)
    }))
)

const tiers = computed(() =>
  mapType('membership_tier')
    .map(tier => {
      const audience = mapRelationship(tier.relationships?.audience?.data)
      const detail = audience && mapRelationship(audience.relationships?.audience_detail?.data)
      return audience
        ? {
            audienceId: String(audience.id),
            name: tier.attributes.tier_name,
            count: detail?.attributes.number_of_contacts ?? 0,
            recurring: tier.attributes.has_cart_subscription_attached === true
          }
        : null
    })
    .filter(Boolean)
)

const recipientRows = computed(() =>
  (wizard.preview.value?.recipient_rows ?? []).map(row => ({ name: row.audience_name, count: row.count }))
)

provideHighlightTerms(
  computed(() => [
    wizard.data.name.trim(),
    ...stores.value.map(s => s.name),
    ...storeMethods.value.map(m => m.name),
    ...tiers.value.map(t => t.name)
  ])
)

// rail

const description = computed(() =>
  describeSetup(wizard.data, {
    stores: stores.value,
    methods: storeMethods.value,
    tiers: tiers.value,
    currentIndex: wizard.step.value.index
  })
)
const rail = computed(() => railRows(description.value.rows, { currentKey: stepKey.value, visited: visited.value }))

const nextDates = computed(() => {
  const preview = wizard.preview.value
  if (!preview?.occurrences?.length) return [{ label: 'No dates yet', closed: false }]
  return preview.occurrences.slice(0, 5).map(occurrence => {
    const date = formatDate(occurrence.production_dates[0], tz.value)
    return { label: occurrence.closed ? `${date} closed` : date, closed: Boolean(occurrence.closed) }
  })
})

const jump = key => {
  wizard.go(key)
  railOpen.value = false
}

// defaults from reference data

const syncStore = () => {
  const known = stores.value.map(s => s.id)
  if (wizard.data.storeId && !known.includes(String(wizard.data.storeId))) wizard.data.storeId = null
  if (known.length === 1 && !wizard.data.storeId) wizard.data.storeId = known[0]
}

const syncMethod = () => {
  const known = storeMethods.value.map(m => m.id)
  if (wizard.data.fulfillment.methodId && !known.includes(wizard.data.fulfillment.methodId))
    wizard.data.fulfillment.methodId = null
  if (known.length === 1 && !wizard.data.fulfillment.methodId && !wizard.data.fulfillment.newMethod)
    wizard.data.fulfillment.methodId = known[0]
  wizard.setFallbackMethodId(known[0] ?? null)
}

watch(() => stores.value.map(s => s.id).join(','), syncStore)
watch(() => [wizard.data.storeId, storeMethods.value.map(m => m.id).join(',')], syncMethod)

// actions

const { state: confirmState, ask, confirm: confirmYes, cancel: confirmNo } = useConfirm()

const startOver = async () => {
  const sure = await ask({
    title: 'Start over?',
    message: 'Everything entered for this drop is cleared. Nothing has been saved yet.',
    confirmLabel: 'Start over',
    danger: true
  })
  if (!sure) return
  wizard.reset()
  visited.value = new Set(['what'])
  syncStore()
  syncMethod()
}

const hasInput = () => Boolean(wizard.data.name.trim()) || stepKey.value !== 'what'

const cancel = async () => {
  const sure =
    !hasInput() ||
    (await ask({
      title: 'Discard this drop?',
      message: `Nothing has been saved, so everything entered for ${dropLabel(wizard.data)} is lost.`,
      confirmLabel: 'Discard',
      cancelLabel: 'Keep editing',
      danger: true
    }))
  if (!sure) return
  wizard.reset()
  router.push('/store/drops')
}

// Create needs a preview that answers the current draft, not an older one.
const canCreate = computed(
  () => wizard.canReview.value && wizard.previewOk.value && (wizard.preview.value?.occurrences?.length ?? 0) > 0
)

const create = async () => {
  if (!canCreate.value || wizard.saving.value) return
  const id = await wizard.submit()
  if (!id) return
  showToast('Drop created')
  router.push(`/store/drops/${id}`)
}

const advance = () => (onReview.value ? create() : wizard.next())

const onKeydown = event => {
  if (event.key !== 'Enter' || event.defaultPrevented) return
  if (event.target?.closest?.(ENTER_OWNERS)) return
  event.preventDefault()
  advance()
}

onMounted(async () => {
  window.addEventListener('keydown', onKeydown)
  await Promise.all(
    [api.call('GET', STORES), api.call('GET', METHODS), api.call('GET', TIERS)].map(p => p.catch(() => null))
  )
  syncStore()
  syncMethod()
})
onBeforeUnmount(() => window.removeEventListener('keydown', onKeydown))

__expose({ wizard })

return (_ctx, _cache) => {
  const _component_router_link = _resolveComponent("router-link")

  return (_openBlock(), _createBlock(SecondaryNav, { "full-width": "" }, {
    top: _withCtx(() => [
      _createElementVNode("header", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("nav", _hoisted_3, [
            _createVNode(_component_router_link, {
              to: "/store/drops",
              class: "hover:text-gray-900"
            }, {
              default: _withCtx(() => [...(_cache[2] || (_cache[2] = [
                _createTextVNode("Drops", -1)
              ]))]),
              _: 1
            }),
            _cache[3] || (_cache[3] = _createElementVNode("span", {
              "aria-hidden": "true",
              class: "mx-1"
            }, "/", -1)),
            _cache[4] || (_cache[4] = _createElementVNode("span", null, "New drop", -1))
          ]),
          _createElementVNode("div", _hoisted_4, [
            _createElementVNode("h1", _hoisted_5, _toDisplayString(title.value), 1),
            _cache[5] || (_cache[5] = _createElementVNode("span", {
              "data-draft-pill": "",
              class: "flex-shrink-0 rounded-full bg-[#fef3c7] px-[9px] py-[3px] text-xs font-semibold text-[#92400e]"
            }, " Draft · not saved ", -1))
          ])
        ]),
        _createElementVNode("div", _hoisted_6, [
          _createVNode(WizardButton, {
            class: "min-[1100px]:hidden",
            "aria-expanded": railOpen.value,
            onClick: _cache[0] || (_cache[0] = $event => (railOpen.value = !railOpen.value))
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(railButtonLabel.value), 1)
            ]),
            _: 1
          }, 8, ["aria-expanded"]),
          _createVNode(WizardButton, { onClick: cancel }, {
            default: _withCtx(() => [...(_cache[6] || (_cache[6] = [
              _createTextVNode("Cancel", -1)
            ]))]),
            _: 1
          })
        ])
      ])
    ]),
    main: _withCtx(() => [
      _createElementVNode("div", _hoisted_7, [
        _createElementVNode("div", _hoisted_8, [
          _createElementVNode("div", _hoisted_9, [
            _createElementVNode("div", _hoisted_10, [
              _createElementVNode("div", _hoisted_11, [
                _createElementVNode("p", _hoisted_12, _toDisplayString(eyebrow.value), 1),
                _createElementVNode("h2", _hoisted_13, _toDisplayString(heading.value), 1),
                (subtitle.value)
                  ? (_openBlock(), _createElementBlock("p", _hoisted_14, _toDisplayString(subtitle.value), 1))
                  : _createCommentVNode("", true)
              ]),
              (_unref(wizard).previewError.value)
                ? (_openBlock(), _createElementBlock("p", _hoisted_15, _toDisplayString(_unref(wizard).previewError.value), 1))
                : _createCommentVNode("", true),
              _createElementVNode("div", _hoisted_16, [
                (stepKey.value === 'what')
                  ? (_openBlock(), _createBlock(StepWhat, {
                      key: 0,
                      stores: stores.value
                    }, null, 8, ["stores"]))
                  : (stepKey.value === 'often')
                    ? (_openBlock(), _createBlock(StepOften, { key: 1 }))
                    : (stepKey.value === 'day')
                      ? (_openBlock(), _createBlock(StepDay, {
                          key: 2,
                          tz: tz.value,
                          "store-name": storeName.value
                        }, null, 8, ["tz", "store-name"]))
                      : (stepKey.value === 'get')
                        ? (_openBlock(), _createBlock(StepGet, {
                            key: 3,
                            methods: storeMethods.value
                          }, null, 8, ["methods"]))
                        : (stepKey.value === 'close')
                          ? (_openBlock(), _createBlock(StepClose, { key: 4 }))
                          : (stepKey.value === 'who')
                            ? (_openBlock(), _createBlock(StepWho, {
                                key: 5,
                                tiers: tiers.value,
                                tz: tz.value
                              }, null, 8, ["tiers", "tz"]))
                            : (stepKey.value === 'pay')
                              ? (_openBlock(), _createBlock(StepPay, {
                                  key: 6,
                                  "recipient-rows": recipientRows.value
                                }, null, 8, ["recipient-rows"]))
                              : (_openBlock(), _createBlock(StepReview, {
                                  key: 7,
                                  stores: stores.value,
                                  methods: storeMethods.value,
                                  tiers: tiers.value,
                                  tz: tz.value,
                                  errors: _unref(wizard).errors.value
                                }, null, 8, ["stores", "methods", "tiers", "tz", "errors"]))
              ])
            ])
          ]),
          _createElementVNode("div", _hoisted_17, [
            _createVNode(WizardNav, {
              "show-back": _unref(wizard).step.value.index > 0,
              "primary-label": primaryLabel.value,
              "primary-enabled": onReview.value ? canCreate.value : _unref(wizard).valid.value,
              loading: _unref(wizard).saving.value,
              arrow: !onReview.value,
              onBack: _unref(wizard).back,
              onPrimary: advance
            }, null, 8, ["show-back", "primary-label", "primary-enabled", "loading", "arrow", "onBack"])
          ])
        ]),
        (railOpen.value)
          ? (_openBlock(), _createElementBlock("div", {
              key: 0,
              class: "fixed inset-0 z-30 bg-black/20 min-[1100px]:hidden",
              onClick: _cache[1] || (_cache[1] = $event => (railOpen.value = false))
            }))
          : _createCommentVNode("", true),
        _createElementVNode("div", {
          "data-so-far": "",
          class: _normalizeClass(["w-80 flex-shrink-0 border-l border-gray-200 bg-white min-[1100px]:static min-[1100px]:block min-[1100px]:shadow-none", railOpen.value ? 'fixed inset-y-0 right-0 z-40 overflow-y-auto shadow-xl' : 'hidden'])
        }, [
          _createVNode(WizardRail, {
            rows: rail.value,
            title: "Setup",
            onJump: jump
          }, {
            default: _withCtx(() => [
              _cache[8] || (_cache[8] = _createElementVNode("p", { class: "m-0 text-[13px] text-gray-500" }, "Nothing is saved until you press Create drop.", -1)),
              _createElementVNode("button", {
                type: "button",
                class: "mt-2 text-[13px] text-gray-600 underline hover:text-gray-900",
                onClick: startOver
              }, " Start over "),
              _createElementVNode("div", _hoisted_18, [
                _cache[7] || (_cache[7] = _createElementVNode("p", { class: "m-0 mb-2 text-xs font-semibold text-gray-500" }, "Next dates", -1)),
                _createElementVNode("div", _hoisted_19, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(nextDates.value, (chip) => {
                    return (_openBlock(), _createElementBlock("span", {
                      key: chip.label,
                      class: _normalizeClass(["rounded-full px-2 py-px text-xs font-medium", chip.closed ? 'bg-[#f3f4f6] text-gray-500 line-through' : 'bg-[#ecfdf5] text-[#065f46]'])
                    }, _toDisplayString(chip.label), 3))
                  }), 128))
                ])
              ])
            ]),
            _: 1
          }, 8, ["rows"])
        ], 2)
      ]),
      _createVNode(WizardConfirmModal, _mergeProps(_unref(confirmState), {
        onConfirm: _unref(confirmYes),
        onCancel: _unref(confirmNo)
      }), null, 16, ["onConfirm", "onCancel"])
    ]),
    _: 1
  }))
}
}

}